import { useEffect, useRef, useState } from 'react';
import type { FileAttachment, FileTarget } from '@forecastify/shared';
import { Button, Dialog, Icon, SKELETON_WIDTHS, Skeleton, skeletonWidth } from '@forecastify/ui';
import { useT } from '../i18n';
import { useSession } from '../session';
/* ⚠️ C47 · extracted, because the Agent's chips need the same mechanism and a different shape. The
 * header's warning about a second list was about the download and the refusals drifting; this is
 * what keeps them from drifting while the shape differs. */
import { SectionAction, SectionHeader } from './contracts/detail-chrome';
import { DetailBox } from './card';
import { downloadAttachment, readableFileSize } from './file-helpers';
import { DropZone } from './drop-zone';
import { useFileDrop } from './file-drop';
import { DOCUMENT_FILES } from './file-kinds';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C22 · attachments on a comment. `decisions.md` R-20.                      │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **BESIDE THE COMMENT, NOT INSIDE ITS MARKUP, AND THAT IS THE POINT.** R-18 named the hazard
 * three chunks ago: allowing `img` and `src` in `comment-markup.ts` is *"the moment
 * `ALLOWED_URI_REGEXP` stops being adequate on its own, because a `src` can name any host on the
 * internet."* A list beside the text routes around that entirely — the sanitiser keeps its closed
 * tag set, no attribute is widened, and an attachment cannot be a vector for anything the editor
 * writes.
 *
 * ⚠️ **The bytes never come from a URL the server minted.** The client builds one from the id
 * against its own API base, which is the same rule `fileAttachmentSchema` states: a URL on the wire
 * is a second place that knows how files are addressed, and the one that goes stale.
 */

/** Fetches through the session, so the bearer token and the workspace header are attached. */
/**
 * ⚠️ **Exported since the Agent's composer needs it too.** A pending attachment there is shown the
 * same way, for the same reason — an `<img src>` cannot carry an Authorization header — and a
 * second copy of this would be a second place that forgets to revoke the URL.
 */
export function useObjectUrl(attachment: FileAttachment | null): string | null {
  const session = useSession();
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (attachment === null) return;

    let revoked: string | null = null;
    const abort = new AbortController();

    void (async () => {
      const response = await session
        .request(`/files/${attachment.id}`, { signal: abort.signal })
        .catch(() => null);
      if (response?.ok !== true || abort.signal.aborted) return;

      /**
       * ⚠️ **A blob URL, because an `<img src>` cannot carry an Authorization header.** The same
       * constraint that made C21 read its stream with `fetch` rather than `EventSource`: putting
       * the token in a query string would write it into every access log. So the bytes are
       * fetched with the session's own request and handed to the browser as an object URL.
       */
      const blob = await response.blob();
      revoked = URL.createObjectURL(blob);
      setUrl(revoked);
    })();

    return () => {
      abort.abort();
      /* ⚠️ Revoked on unmount. A blob URL pins its bytes in memory until it is released, so a
       * task detail opened forty times would hold forty images. */
      if (revoked !== null) URL.revokeObjectURL(revoked);
    };
  }, [attachment, session]);

  return url;
}

/**
 * The full-size overlay.
 *
 * ⚠️ **Escape closes it, and the backdrop closes it.** `state-rules.md` and every other overlay in
 * the product; an image that can only be dismissed by a small × is one people close by reloading.
 *
 * ⚠️ **Exported since the Agent's composer opens it too.** Its attachment cards had no preview at
 * all and clicking one downloaded the file somebody had just uploaded from their own disk — the
 * answer to *"which screenshot did I attach"* is to show it, and this already shows it correctly.
 */
export function Lightbox({
  attachment,
  onClose,
}: {
  readonly attachment: FileAttachment;
  readonly onClose: () => void;
}) {
  const t = useT();
  const url = useObjectUrl(attachment);
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      /* ⚠️ 28 Oct 2026 · captured and handled, so a task modal behind the viewer hears an Escape
         already spent and stays open (`useModalSurface`). */
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    };
    globalThis.addEventListener('keydown', onKey, true);
    /* ⚠️ Focus moves into the overlay, or the keyboard is still on the page behind it. */
    closeRef.current?.focus();
    return () => {
      globalThis.removeEventListener('keydown', onKey, true);
    };
  }, [onClose]);

  return (
    <div
      className="bg-[color:var(--scrim)] fixed inset-0 z-50 flex items-center justify-center p-8"
      role="dialog"
      aria-modal="true"
      aria-label={attachment.filename}
      onClick={onClose}
    >
      <button
        ref={closeRef}
        type="button"
        onClick={onClose}
        aria-label={t('common.close')}
        className="bg-card text-foreground absolute top-4 right-4 grid size-8 place-items-center rounded-md border"
      >
        <Icon name="x" size={16} />
      </button>

      {url === null ? (
        <p className="text-[color:var(--on-id-light)] text-[12.5px]">{t('common.loading')}</p>
      ) : (
        /* ⚠️ `stopPropagation`, so clicking the picture itself does not close the thing showing it. */
        <img
          src={url}
          alt={attachment.filename}
          className="max-h-full max-w-full rounded-md object-contain"
          onClick={(event) => {
            event.stopPropagation();
          }}
        />
      )}
    </div>
  );
}

/** One row: a thumbnail for an image, an icon for everything else. */
function AttachmentRow({
  attachment,
  canRemove,
  onOpen,
  onDownload,
  onRemove,
}: {
  readonly attachment: FileAttachment;
  readonly canRemove: boolean;
  readonly onOpen: () => void;
  readonly onDownload: () => void;
  readonly onRemove: () => void;
}) {
  const t = useT();
  /* ⚠️ Only fetched for something that can be shown. A PDF has no preview and the bytes would be
   * downloaded for nothing — on every comment, on every render of the task. */
  const thumbnail = useObjectUrl(attachment.inline ? attachment : null);

  return (
    <li className="border-input flex items-center gap-2 rounded-md border p-1.5">
      {attachment.inline ? (
        <button
          type="button"
          onClick={onOpen}
          aria-label={t('task.attachment.open', { name: attachment.filename })}
          className="bg-muted size-9 shrink-0 overflow-hidden rounded"
        >
          {thumbnail === null ? null : (
            <img src={thumbnail} alt="" className="size-full object-cover" />
          )}
        </button>
      ) : (
        <span className="bg-muted text-muted-foreground grid size-9 shrink-0 place-items-center rounded">
          <Icon name="file-text" size={15} />
        </span>
      )}

      <span className="flex min-w-0 flex-1 flex-col">
        {/* ⚠️ `f.name` is `text-[12.5px] font-medium` in every detail component the mockup ships —
            ContractDetail, TaskDetail, ExtCostDetail. The filename is what the row IS. */}
        <span className="truncate text-[12.5px] font-medium">{attachment.filename}</span>
        <span className="text-muted-foreground text-[11px]">
          {readableFileSize(attachment.byteSize)}
          {attachment.uploadedByName === null ? '' : ` · ${attachment.uploadedByName}`}
        </span>
      </span>

      {/* ⚠️ Download is offered for EVERYTHING, including an image — a preview is not a copy, and
          somebody who wants the file wants the file. */}
      <Button
        variant="ghost"
        size="sm"
        onClick={onDownload}
        aria-label={t('task.attachment.download', { name: attachment.filename })}
      >
        <Icon name="download" size={14} />
      </Button>
      {canRemove ? (
        <Button
          variant="ghost"
          size="sm"
          onClick={onRemove}
          aria-label={t('task.attachment.remove', { name: attachment.filename })}
        >
          <Icon name="trash" size={14} />
        </Button>
      ) : null}
    </li>
  );
}

/**
 * The files attached to one thing — a comment, a contract, whatever `FILE_TARGETS` grows to hold.
 *
 * ⚠️⚠️ **`onUpload` IS OPTIONAL, AND THE COMMENT IS WHY.** On a comment, attaching happens in the
 * COMPOSER before the comment is sent: a comment is a statement somebody made at a moment, and a
 * control that quietly adds a file to it afterwards makes the thread say something different from
 * what was posted. Reported in exactly those terms. A task, a contract, a line item and a person
 * are not statements, so each passes an upload and gets a drop area above the list.
 *
 * ⚠️⚠️ **THIS NOTE USED TO CLAIM THE PROP EXISTED AND IT DID NOT.** The sentence *"which is why
 * `onUpload` is a prop rather than a branch"* described a design nobody had built: the component
 * was read-and-remove, the task section rendered it with no control of any kind, and the only way
 * to attach anything to a ticket was to drag a file onto the page and hope. Reported as *"upload
 * doesn't work for task detail and task itself"* — there was nothing to click.
 *
 * ⚠️ **The TARGET is a parameter, not a second component.** C24 needed the same list on a
 * contract; a copy would have been two places for the download, the lightbox and the four refusal
 * messages to drift.
 */
export function Attachments({
  target,
  targetId,
  canEdit,
  emptyNote,
  onUpload,
  removalNote,
  onRemoved,
  accept = DOCUMENT_FILES,
  heading,
  variant = 'inline',
}: {
  readonly target: FileTarget;
  readonly targetId: string;
  readonly canEdit: boolean;
  /** Shown when there is nothing attached. Omitted on a comment, which says nothing at all. */
  readonly emptyNote?: string;
  /**
   * Given, the list grows a drop area with a browse link. Resolves when the server has answered, so
   * the list can re-read itself — see the note on the component.
   */
  readonly onUpload?: (files: readonly File[]) => Promise<void>;
  /**
   * ┌───────────────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ A REASON TO ASK BEFORE REMOVING, OR `null` TO JUST DO IT.            │
   * └───────────────────────────────────────────────────────────────────────────┘
   *
   * Found on 15 Sep from a report of a broken link and an audit trail: a file was attached to a
   * task at 11:17:18, linked from its description, and deleted from this list at 11:18:54. The
   * description kept the link, the link kept pointing at a row that no longer existed, and the
   * reader got a 404 — which reads as *"the link feature is broken"* rather than *"you deleted the
   * file"*.
   *
   * ⚠️ **Removing from this list is not obviously the same act as removing it from the text**, and
   * it is: one file, two places that mention it. Whoever is deleting has to be told, and then the
   * two have to be kept in step — `onRemoved` is the other half.
   *
   * ⚠️⚠️ **THE CASE THAT PROMPTED IT IS GONE AND THE GUARD IS NOT.** `0117` gave a description its
   * own file target, so a file dropped into the text never appears in this list and the two can no
   * longer be the same row by accident. What remains is somebody PASTING a link to one of these
   * files into the description by hand — rarer, identical in effect, and now caught.
   */
  readonly removalNote?: (attachment: FileAttachment) => string | null;
  /** Called after a successful removal, so a caller can drop its own reference to the file. */
  readonly onRemoved?: (attachment: FileAttachment) => void;
  /** ⚠️ `file-kinds.ts` is the one list; a caller overrides it only for a genuinely different kind. */
  readonly accept?: readonly string[];
  /**
   * ┌────────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ THE SAME LIST, WITHOUT THE LANDING STRIP — feedback round 4 item 12.        │
   * └────────────────────────────────────────────────────────────────────┘
   *
   * Given a `heading`, the component draws the section itself: the title, an **Add** button at the
   * right edge of that row, and one line of hint under the list. `DropZone`'s dashed box — icon,
   * prompt, a sentence of accepted formats and a browse button — is five lines tall and sat above
   * an empty list, which is how a task with no files came to have the tallest block on its page.
   *
   * ⚠️⚠️ **DROPPING STILL WORKS, AND THAT IS THE WHOLE POINT OF THE HINT.** The gesture was
   * never discoverable — the note on `onUpload` below says the strip existed BECAUSE dropping was
   * the only way in. The button is the discoverable way now, so the strip's job is done and the
   * drop target becomes the section itself, which is larger than the strip ever was.
   *
   * ⚠️ Everything else is unchanged: the callers that want the strip (a leave request, a comment)
   * simply do not pass a heading.
   */
  readonly heading?: string;
  /**
   * ┌────────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ WHICH SECTION SHAPE THE PAGE AROUND IT USES.                              │
   * └────────────────────────────────────────────────────────────────────┘
   *
   * ⚠️⚠️ **THE TWO ARE PAGE CONVENTIONS, NOT PREFERENCES, AND ONE COMPONENT CANNOT PICK.** A task
   * draws each section AS a card with a `text-[13px]` heading inside it; a contract, a line item and
   * a leave request draw an uppercase `SectionHeader` OUTSIDE a card that holds the content. Item
   * 12's row was written for the first and put on all five, so on a line item it sat in sentence
   * case above an unboxed list while *SZÁMLÁK* and *KÜLSŐ KÖLTSÉGEK* two inches above it did the
   * opposite. Reported 20 Sep.
   *
   * ⚠️ **`section` is the same `SectionHeader` and `SectionAction` those two use**, and the same
   * `bg-card rounded-xl border shadow-sm` box — imported rather than reproduced, so the three
   * cannot drift.
   *
   * ⚠️ **`box` is a property rail's `DetailBox`** — the person page's — drawn here so the add
   * button can sit in its title strip. The caller drawing the box and this drawing the button
   * inside it put the button at the right edge of the body, under the title it belonged beside.
   */
  readonly variant?: 'inline' | 'section' | 'box';
}) {
  const t = useT();
  const session = useSession();
  const [items, setItems] = useState<readonly FileAttachment[]>([]);
  /** Until the first read lands — skeleton rows where the empty note would otherwise flash. */
  const [loading, setLoading] = useState(true);
  const [showing, setShowing] = useState<FileAttachment | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState<{
    attachment: FileAttachment;
    note: string;
  } | null>(null);
  /** ⚠️ Compact mode only — the button opens it, so it lives here rather than in `DropZone`. */
  const picker = useRef<HTMLInputElement | null>(null);

  const take = (files: readonly File[]): void => {
    if (onUpload === undefined || files.length === 0) return;
    void (async () => {
      setBusy(true);
      await onUpload(files);
      setBusy(false);
      await reload();
    })();
  };

  /* ⚠️ Registered whatever the mode, and only WIRED in compact mode — a hook may not be called
     conditionally. `enabled` is what turns it off for the strip, which has its own. */
  const drop = useFileDrop({
    enabled: heading !== undefined && canEdit && onUpload !== undefined && !busy,
    onFiles: take,
  });

  const reload = async (): Promise<void> => {
    const response = await session
      .request(`/files?target=${target}&targetId=${targetId}`)
      .catch(() => null);
    if (response?.ok !== true) return;
    const body = (await response.json().catch(() => null)) as { data?: FileAttachment[] } | null;
    setItems(body?.data ?? []);
  };

  useEffect(() => {
    void reload().finally(() => {
      setLoading(false);
    });
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- `reload` closes over `session`,
       which is a fresh object every render; listing it re-fetches for ever (C20's ten-requests bug, `preferences-boot.tsx`). */
  }, [target, targetId]);

  const remove = async (attachment: FileAttachment): Promise<void> => {
    await session.request(`/files/${attachment.id}`, { method: 'DELETE' }).catch(() => null);
    onRemoved?.(attachment);
    await reload();
  };

  /* ⚠️ The note decides whether there is anything to ask about; no note, no dialog, and the row
     behaves exactly as it did before this prop existed. */
  const askOrRemove = (attachment: FileAttachment): void => {
    const note = removalNote?.(attachment) ?? null;
    if (note === null) void remove(attachment);
    else setConfirming({ attachment, note });
  };

  /* ⚠️ Nothing at all when there is nothing to show and nothing to do — a comment with no files
   * must not grow an empty strip under it. A caller that wants a sentence passes `emptyNote`. */
  if (items.length === 0 && emptyNote === undefined && onUpload === undefined) return null;

  const mayAdd = canEdit && onUpload !== undefined;

  const body = (
    <div
      {...(heading === undefined ? {} : drop.handlers)}
      className={[
        /* ⚠️⚠️ **NO OWN SPACING IN `section`.** Its parts carry `mt-1.5` themselves, exactly as
           *SZÁMLÁK*'s box does — a gap here as well would put this section's card further from its
           heading than every neighbour's. */
        variant === 'section' ? 'flex flex-col' : 'mt-1 flex flex-col gap-1.5',
        /* ⚠️ The whole section lights up on a drag, which is the compact mode's answer to losing
           the dashed box: the target is bigger than the box was, it just is not drawn until there
           is something to drop. */
        heading === undefined ? '' : 'rounded-md transition-colors',
        drop.over ? 'bg-primary/5 outline-primary outline-2 outline-dashed outline-offset-4' : '',
      ].join(' ')}
    >
      {heading === undefined || variant !== 'inline' ? null : (
        /* ⚠️ The same row *kapcsolódó feladatok* uses — title, spacer, action — so the two
           sections under a task read as one pattern rather than as two designs.

           ⚠️ An EMPTY heading means *compact, but somebody else drew the title*: the person page's
           `DetailBox` names the section already, and a second copy inside it would be the heading
           twice. The row still carries the button. */
        <h3 className="mb-0.5 flex items-center gap-2 text-[13px] font-medium">
          {heading}
          <div className="flex-1" />
          {!mayAdd ? null : (
            <Button
              variant="ghost"
              size="sm"
              disabled={busy}
              onClick={() => {
                picker.current?.click();
              }}
            >
              <Icon name="plus" size={13} />
              {busy ? t('upload.busy') : t('task.attachment.add')}
            </Button>
          )}
        </h3>
      )}

      {heading === undefined || variant !== 'section' ? null : (
        <SectionHeader title={heading}>
          {!mayAdd ? null : (
            <SectionAction
              label={busy ? t('upload.busy') : t('task.attachment.add')}
              onClick={() => {
                picker.current?.click();
              }}
            />
          )}
        </SectionHeader>
      )}

      {heading === undefined || !mayAdd ? null : (
        <input
          ref={picker}
          type="file"
          multiple
          accept={accept.join(',')}
          className="hidden"
          disabled={busy}
          onChange={(event) => {
            const files = [...(event.target.files ?? [])];
            /* ⚠️ Cleared, so choosing the same file twice still fires `change` — `DropZone` says
               why: otherwise a failed upload cannot be retried with the same file. */
            event.target.value = '';
            take(files);
          }}
        />
      )}

      {/* ⚠️ Above the list, not below it: the thing somebody came to do is add a file, and a
          control under a list of forty is a control they scroll past. */}
      {heading !== undefined || onUpload === undefined || !canEdit ? null : (
        <DropZone
          accept={accept}
          busy={busy}
          onFiles={(files) => {
            take(files);
          }}
        />
      )}
      {/* ⚠️⚠️ **THE EMPTY STATE IS THE BOX ITSELF ON A SECTION PAGE**, exactly as *SZÁMLÁK* and
          *KÜLSŐ KÖLTSÉGEK* draw theirs — centred, in the card, rather than a sentence floating under
          a heading. That difference was most of what the report was about. */}
      {items.length !== 0 || !loading ? null : (
        <>
          <ul
            aria-hidden
            className={
              variant === 'section'
                ? 'bg-card mt-1.5 flex flex-col gap-1 rounded-xl border p-2 shadow-sm'
                : 'flex flex-col gap-1'
            }
          >
            {SKELETON_WIDTHS.slice(0, 2).map((_widths, index) => (
              <li
                key={`skeleton-${String(index)}`}
                className="border-input flex items-center gap-2 rounded-md border p-1.5"
              >
                <Skeleton className="size-9 shrink-0 rounded" />
                <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                  <Skeleton width={skeletonWidth(index, 0)} />
                  <Skeleton width={skeletonWidth(index, 1) / 2} className="h-2.5" />
                </div>
              </li>
            ))}
          </ul>
          <div role="status" aria-live="polite" className="sr-only">
            {t('state.loading')}
          </div>
        </>
      )}
      {items.length !== 0 || loading ? null : variant === 'section' ? (
        <p className="text-muted-foreground bg-card mt-1.5 rounded-xl border p-4 text-center text-[12.5px] shadow-sm">
          {emptyNote}
        </p>
      ) : (
        <p className="text-muted-foreground text-[11.5px]">{emptyNote}</p>
      )}

      {items.length === 0 ? null : (
        <ul
          className={
            variant === 'section'
              ? 'bg-card mt-1.5 flex flex-col gap-1 rounded-xl border p-2 shadow-sm'
              : 'flex flex-col gap-1'
          }
        >
          {items.map((attachment) => (
            <AttachmentRow
              key={attachment.id}
              attachment={attachment}
              canRemove={canEdit}
              onOpen={() => {
                setShowing(attachment);
              }}
              onDownload={() => void downloadAttachment(session, attachment)}
              onRemove={() => {
                askOrRemove(attachment);
              }}
            />
          ))}
        </ul>
      )}

      {/**
       * ⚠️⚠️ **THE *"vagy húzd ide a fájlokat"* LINE IS GONE — asked for by name.** It
       * explained a gesture, under a block whose two neighbours explain nothing; a hint that only
       * one of three sibling sections carries is the line that makes it the odd one out. The drop
       * still works everywhere it worked before — it simply stops announcing itself.
       */}

      {confirming === null ? null : (
        <Dialog
          open
          onOpenChange={(next) => {
            if (!next) setConfirming(null);
          }}
          title={t('task.attachment.removeTitle')}
          closeLabel={t('common.close')}
          width={460}
        >
          <div className="flex flex-col gap-3">
            <p className="text-[13px] leading-relaxed">{confirming.note}</p>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setConfirming(null);
                }}
              >
                {t('common.cancel')}
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  const { attachment } = confirming;
                  setConfirming(null);
                  void remove(attachment);
                }}
              >
                {t('task.attachment.remove', { name: confirming.attachment.filename })}
              </Button>
            </div>
          </div>
        </Dialog>
      )}

      {showing === null ? null : (
        <Lightbox
          attachment={showing}
          onClose={() => {
            setShowing(null);
          }}
        />
      )}
    </div>
  );

  if (variant !== 'box') return body;
  return (
    <DetailBox
      title={heading ?? ''}
      actions={
        !mayAdd ? undefined : (
          <Button
            variant="ghost"
            size="sm"
            disabled={busy}
            onClick={() => {
              picker.current?.click();
            }}
          >
            <Icon name="plus" size={13} />
            {busy ? t('upload.busy') : t('task.attachment.add')}
          </Button>
        )
      }
    >
      <div className="px-1.5 py-1">{body}</div>
    </DetailBox>
  );
}
